import { ALL_BOOKINGS_OPTION, EventReportBookingOptions } from '@contexts/event-report/event-report-context.type';
import {
  EventChannelsSalesStatsResponse,
  EventReportDataType,
  EventSalesOffersChannel,
  EventSalesStatsResponse,
} from '@domain/event-report';
import { TFunction } from 'i18next';

import {
  BADGES_CHECKIN_LIST,
  CHANNEL_DATA_TYPE_TABS,
  CHANNEL_MODE_TYPE_TABS,
  CHANNEL_TABLE_COLUMNS,
  CHANNEL_TABLE_COLUMNS_PRE_REGISTRATION,
  CHANNEL_TABLE_COLUMNS_REVENUE,
  ChannelDataTypeTabs,
  EVENT_REPORT_CHECKIN_TYPE,
  EVENT_REPORT_MODE_TYPE_GRAPH,
  EVENT_REPORT_TAB_PRE_REGISTRATION,
  EVENT_REPORT_TAB_TYPE_ATTENDEES,
  EVENT_REPORT_TAB_TYPE_BOOKING,
  EVENT_REPORT_TAB_TYPE_REVENUE,
  EVENT_REPORT_TABLE_CHANNELS_COLUMN,
  EVENT_REPORT_TYPES,
  PAST_CHANNEL_DATA_TYPE_TABS,
  PAST_CHANNEL_TABLE_COLUMNS,
} from './config';
import { CHANNEL_TABLE_COLUMNS_TYPES, TableChannelReport } from './types';
import { createDefaultAdmissionBadges, createFilteredAdmissionBadges } from '.';

const createDefaultTabs = (t: TFunction, isLiveEvent: boolean) => {
  return {
    dataTypeTabs: (isLiveEvent ? CHANNEL_DATA_TYPE_TABS : PAST_CHANNEL_DATA_TYPE_TABS).map((tab) => ({
      ...tab,
      name: t(tab.name),
    })),
    defaultDataTypeTab: EVENT_REPORT_TAB_TYPE_BOOKING,
    modeTypeTabs: CHANNEL_MODE_TYPE_TABS,
    defaultModeTypeTab: EVENT_REPORT_MODE_TYPE_GRAPH,
  };
};

const createDefaultDataSection = (t: TFunction, isLiveEvent: boolean) => {
  return {
    data: [],
    chartData: [],
    categories: createDefaultAdmissionBadges(t),
    tableColumns: {
      type: '',
      isDropdown: false,
      isFooterEnabled: false,
      columns: [],
      expandColumns: [],
    },
    ...createDefaultTabs(t, isLiveEvent),
  };
};

const getEventSalesData = (
  type: EventReportDataType,
  eventSales: EventSalesStatsResponse,
  bookingFilterValue: EventReportBookingOptions
) => ({
  [`total ${type} ${EVENT_REPORT_TAB_TYPE_BOOKING}`]:
    eventSales[type].soldItems[bookingFilterValue] + eventSales[type].addOns.soldItems[bookingFilterValue],
  [`total ${type} ${EVENT_REPORT_TAB_TYPE_REVENUE}`]:
    eventSales[type].revenue[bookingFilterValue] + eventSales[type].addOns.revenue[bookingFilterValue],
  [`total ${type} ${EVENT_REPORT_TAB_TYPE_ATTENDEES}`]: eventSales[type].attendees[bookingFilterValue],
});

const getOffersChannelsData = (
  type: EventReportDataType,
  offersChannel: EventSalesOffersChannel,
  bookingFilterValue: EventReportBookingOptions
) => ({
  [`${type} ${EVENT_REPORT_TAB_TYPE_BOOKING}`]: offersChannel[type].soldItems[bookingFilterValue],
  [`${type} ${EVENT_REPORT_TAB_TYPE_REVENUE}`]: offersChannel[type].revenue[bookingFilterValue],
  [`${type} ${EVENT_REPORT_TAB_TYPE_ATTENDEES}`]: offersChannel[type].attendees[bookingFilterValue],
});

const createChartData = (
  data: EventChannelsSalesStatsResponse,
  eventSales: EventSalesStatsResponse,
  bookingFilterValue: EventReportBookingOptions
) => {
  return data.map((channel) => {
    let result = {
      channel: channel.channelName,
      revenue: channel.revenue[bookingFilterValue],
      soldItems: channel.soldItems[bookingFilterValue],
      attendees: channel.attendees[bookingFilterValue],
    };
    EVENT_REPORT_TYPES.forEach((type) => {
      result = {
        ...result,
        ...getEventSalesData(type, eventSales, bookingFilterValue),
        ...getOffersChannelsData(type, channel.offers, bookingFilterValue),
      };
    });
    return result;
  });
};

const selectTableTypes = (isLiveEvent: boolean, type: ChannelDataTypeTabs) => {
  if (isLiveEvent && type === EVENT_REPORT_TAB_TYPE_BOOKING) {
    return CHANNEL_TABLE_COLUMNS;
  }
  if (!isLiveEvent && (type === EVENT_REPORT_TAB_TYPE_BOOKING || type === EVENT_REPORT_TAB_TYPE_ATTENDEES)) {
    return PAST_CHANNEL_TABLE_COLUMNS;
  }
  if (type === EVENT_REPORT_TAB_TYPE_REVENUE) {
    return CHANNEL_TABLE_COLUMNS_REVENUE;
  }

  if (type === EVENT_REPORT_TAB_PRE_REGISTRATION) {
    return CHANNEL_TABLE_COLUMNS_PRE_REGISTRATION;
  }

  return CHANNEL_TABLE_COLUMNS;
};

const selectExpandColumnsTypes = (type: ChannelDataTypeTabs) => {
  if (type === EVENT_REPORT_TAB_PRE_REGISTRATION) return [];

  return EVENT_REPORT_TYPES;
};

/* CONFIG CHANNEL TABLE */
export const createChannelsTableColumns: (isLiveEvent: boolean, type: ChannelDataTypeTabs) => TableChannelReport = (
  isLiveEvent: boolean,
  type: ChannelDataTypeTabs
) => {
  return {
    type: EVENT_REPORT_TABLE_CHANNELS_COLUMN,
    isDropdown: false,
    isFooterEnabled: false,
    columns: selectTableTypes(isLiveEvent, type),
    expandColumns: selectExpandColumnsTypes(type),
  };
};

const createTableColumns = (data: EventChannelsSalesStatsResponse, isLiveEvent: boolean, type: ChannelDataTypeTabs) => {
  const channelsTableColumns = createChannelsTableColumns(isLiveEvent, type);
  return {
    ...channelsTableColumns,
    columns: channelsTableColumns.columns.flatMap((column) => {
      if (channelsTableColumns.expandColumns.includes(column.id as CHANNEL_TABLE_COLUMNS_TYPES)) {
        const hasAdmissions = data[0].offers[column.id as EventReportDataType].offers.length > 0;

        if (!hasAdmissions && column.id !== EVENT_REPORT_CHECKIN_TYPE) return [];

        return [
          column,
          ...data[0].offers[column.id as EventReportDataType].offers.map((offer) => ({
            id: JSON.stringify({ type: column.id, id: offer.id, name: offer.name }),
            isActive: false,
            dependsOn: column.id,
            isClickable: true,
          })),
        ];
      }

      return column;
    }),
  };
};

export const formatDataChannelSection = ({
  data,
  eventSales,
  bookingFilterValue = ALL_BOOKINGS_OPTION,
  t,
  isLiveEvent,
  tab,
}: {
  data: EventChannelsSalesStatsResponse;
  eventSales: EventSalesStatsResponse;
  t: TFunction<'translation', undefined>;
  bookingFilterValue?: EventReportBookingOptions;
  tab: ChannelDataTypeTabs;
  isLiveEvent: boolean;
}) => {
  if (!data.length) return createDefaultDataSection(t, isLiveEvent);

  const sortedData = [...data].sort((a, b) => {
    if (tab === EVENT_REPORT_TAB_PRE_REGISTRATION) return b[tab].bookings - a[tab].bookings;

    return b[tab][bookingFilterValue] - a[tab][bookingFilterValue];
  });

  return {
    data: sortedData,
    chartData: createChartData(sortedData, eventSales, bookingFilterValue),
    categories: createFilteredAdmissionBadges(t, eventSales, bookingFilterValue, BADGES_CHECKIN_LIST),
    tableColumns: createTableColumns(data, isLiveEvent, tab),
    ...createDefaultTabs(t, isLiveEvent),
  };
};
